import React from 'react';
import { Link } from 'react-router-dom';
import {
  Phone,
  MessageSquare,
  Facebook,
  Globe,
  Users,
  Workflow,
  GitBranch,
  Mail,
  UserCog,
  Shield,
  Rocket,
  ArrowRight
} from 'lucide-react';

const DocsHome: React.FC = () => {
  const quickLinks = [
    {
      title: 'Quick Start Guide',
      description: 'Get up and running with your CRM in minutes',
      icon: <Rocket className="w-6 h-6" />,
      link: '/docs/getting-started',
      color: 'blue'
    },
    {
      title: 'Phone Integration',
      description: 'Connect Knowlarity, Mcube, Ozonetel, or TCPN',
      icon: <Phone className="w-6 h-6" />,
      link: '/docs/integrations/phone',
      color: 'green'
    },
    {
      title: 'Lead Management',
      description: 'Capture, track, and manage leads effectively',
      icon: <Users className="w-6 h-6" />,
      link: '/docs/lead-management',
      color: 'purple'
    },
    {
      title: 'Drip Campaigns',
      description: 'Automate email sequences and nurture leads',
      icon: <Mail className="w-6 h-6" />,
      link: '/docs/drip-campaigns',
      color: 'orange'
    }
  ];

  const integrations = [
    { name: 'Phone (Knowlarity, Mcube, Ozonetel, TCPN)', icon: <Phone className="w-5 h-5" />, link: '/docs/integrations/phone' },
    { name: 'WhatsApp (WATI, AiSensy)', icon: <MessageSquare className="w-5 h-5" />, link: '/docs/integrations/whatsapp' },
    { name: 'Facebook Pages & Lead Ads', icon: <Facebook className="w-5 h-5" />, link: '/docs/integrations/facebook' },
    { name: 'Website Forms', icon: <Globe className="w-5 h-5" />, link: '/docs/integrations/website' }
  ];

  const features = [
    { name: 'Lead Management', icon: <Users className="w-5 h-5" />, link: '/docs/lead-management' },
    { name: 'Pipeline & Stages', icon: <Workflow className="w-5 h-5" />, link: '/docs/pipeline' },
    { name: 'Round Robin Assignment', icon: <GitBranch className="w-5 h-5" />, link: '/docs/round-robin' },
    { name: 'Drip Campaigns', icon: <Mail className="w-5 h-5" />, link: '/docs/drip-campaigns' },
    { name: 'User Management', icon: <UserCog className="w-5 h-5" />, link: '/docs/user-management' },
    { name: 'Roles & Permissions', icon: <Shield className="w-5 h-5" />, link: '/docs/roles-permissions' }
  ];

  return (
    <div className="p-8 pb-12">
      <div className="space-y-8">
        {/* Header */}
        <div className="text-center">
        <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
          Welcome to CRM Documentation
        </h1>
        <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
          Comprehensive guides and documentation to help you get the most out of your CRM system.
          Learn how to capture leads, manage pipelines, and automate your sales process.
        </p>
      </div>

      {/* Quick Links */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-12">
        {quickLinks.map((item, index) => (
          <Link
            key={index}
            to={item.link}
            className={`group p-6 bg-white dark:bg-gray-800 border-2 border-${item.color}-200 dark:border-${item.color}-800 rounded-xl hover:shadow-lg transition-all`}
          >
            <div className={`flex items-center justify-center w-12 h-12 rounded-lg bg-${item.color}-100 dark:bg-${item.color}-900 text-${item.color}-600 dark:text-${item.color}-400 mb-4`}>
              {item.icon}
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
              {item.title}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">{item.description}</p>
            <div className="flex items-center text-blue-600 dark:text-blue-400 font-medium">
              <span>Learn more</span>
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </div>
          </Link>
        ))}
      </div>

      {/* Integrations Section */}
      <div className="mt-12">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Integrations</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {integrations.map((item, index) => (
            <Link
              key={index}
              to={item.link}
              className="flex items-center space-x-3 p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-500 dark:hover:border-blue-500 hover:shadow-md transition-all"
            >
              <div className="flex-shrink-0 text-blue-600 dark:text-blue-400">{item.icon}</div>
              <span className="font-medium text-gray-900 dark:text-white">{item.name}</span>
              <ArrowRight className="w-4 h-4 ml-auto text-gray-400" />
            </Link>
          ))}
        </div>
      </div>

      {/* Features Section */}
      <div className="mt-12">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Core Features</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {features.map((item, index) => (
            <Link
              key={index}
              to={item.link}
              className="flex items-center space-x-3 p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-500 dark:hover:border-blue-500 hover:shadow-md transition-all"
            >
              <div className="flex-shrink-0 text-purple-600 dark:text-purple-400">{item.icon}</div>
              <span className="font-medium text-gray-900 dark:text-white">{item.name}</span>
              <ArrowRight className="w-4 h-4 ml-auto text-gray-400" />
            </Link>
          ))}
        </div>
      </div>

      {/* Help Section */}
      <div className="mt-12 p-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl">
        <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-200 mb-2">Need Help?</h3>
        <p className="text-blue-800 dark:text-blue-300 mb-4">
          Can't find what you're looking for? Our support team is here to help.
        </p>
        <div className="flex flex-wrap gap-3">
          <a
            href="mailto:support@yourcrm.com"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Contact Support
          </a>
          <Link
            to="/docs/getting-started"
            className="px-4 py-2 bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 border border-blue-600 dark:border-blue-400 rounded-lg hover:bg-blue-50 dark:hover:bg-gray-700 transition-colors"
          >
            Quick Start Guide
          </Link>
        </div>
      </div>
      </div>
    </div>
  );
};

export default DocsHome;
